import type { TareLocalPrefs } from "../../shared/native-modules/types/TareBridge";

import { localPrefs } from "../../shared/native-modules/tare/getLocalPrefs";
import { dislikeJob as callDislikeJobAPI } from "../native-module/dislikeJob";

import React, { createContext, useContext, useState, type ReactNode, useEffect } from "react";
import { z } from "zod";

// The implementation shares the same data source used by Job Card in ACE iOS, ensuring consistent state synchronization across all interfaces.
// cf: https://indeed.sourcegraph.com/code.corp.indeed.com/mobile-app-platform/mobile-app-platform@e08920d34d6fec67d354b0512a1ad56f9820d0d8/-/blob/apps/jsma-ios/TAREPackages/shared-lib/shared-ui-lib/ios/Sources/SharedUILib/JobCard/JobCardLocalStorage.swift?L30-37
export function getDislikedJobLocalPrefs(localPrefs: TareLocalPrefs) {
  return {
    async getDislikedJobs() {
      const raw = await localPrefs.getString("", "dislikedJobs", "[]");
      if (!raw) {
        return [];
      }
      try {
        return z.array(z.string()).parse(JSON.parse(raw));
      } catch {
        return [];
      }
    },
    async addDislikedJob(jobKey: string) {
      const prevJobKeys = await this.getDislikedJobs();
      const newJobKeys = prevJobKeys.filter((a) => a !== jobKey);
      newJobKeys.push(jobKey);
      await localPrefs.setString("", "dislikedJobs", JSON.stringify(newJobKeys));
    },
    async removeDislikedJob(jobKey: string) {
      const prevJobKeys = await this.getDislikedJobs();
      const newJobKeys = prevJobKeys.filter((a) => a !== jobKey);
      await localPrefs.setString("", "dislikedJobs", JSON.stringify(newJobKeys));
    },
  };
}
const dislikedJobLocalPrefs = getDislikedJobLocalPrefs(localPrefs);

interface DislikedJobsContextType {
  dislikedJobs: Set<string>;
  dislikeJob: (jobKey: string, dislikeJobPageId: string, tk: string | undefined) => Promise<void>;
  undoDislikeJob: (
    jobKey: string,
    dislikeJobPageId: string,
    tk: string | undefined,
  ) => Promise<void>;
}

const DislikedJobsContext = createContext<DislikedJobsContextType>({
  dislikedJobs: new Set(),
  dislikeJob: async () => {},
  undoDislikeJob: async () => {},
});

export const DislikedJobsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dislikedJobs, setDislikedJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    dislikedJobLocalPrefs
      .getDislikedJobs()
      .then((jobKeys) => {
        if (!cancelled) {
          setDislikedJobs(new Set(jobKeys));
        }
      })
      .catch((e) => console.error("Failed to load disliked jobs:", e));
    return () => {
      cancelled = true;
    };
  }, []);

  const dislikeJob = async (jobKey: string, dislikeJobPageId: string, tk?: string) => {
    setDislikedJobs((prev) => {
      const newSet = new Set(prev);
      newSet.add(jobKey);
      return newSet;
    });

    // Invoke API
    await callDislikeJobAPI(jobKey, dislikeJobPageId, false, tk).catch((e) => console.error(e));
    // Data Persistence
    await dislikedJobLocalPrefs.addDislikedJob(jobKey).catch((e) => console.error(e));
  };

  const undoDislikeJob = async (jobKey: string, dislikeJobPageId: string, tk?: string) => {
    setDislikedJobs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(jobKey);
      return newSet;
    });

    // Invoke API
    await callDislikeJobAPI(jobKey, dislikeJobPageId, true, tk).catch((e) => console.error(e));
    // Data Persistence
    await dislikedJobLocalPrefs.removeDislikedJob(jobKey).catch((e) => console.error(e));
  };

  const contextValue: DislikedJobsContextType = {
    dislikedJobs,
    dislikeJob,
    undoDislikeJob,
  };

  return (
    <DislikedJobsContext.Provider value={contextValue}>{children}</DislikedJobsContext.Provider>
  );
};

export const useDislikedJobsContext = () => {
  return useContext(DislikedJobsContext);
};
